package com.nonopn.skydome.common.app.pages.main.model

import com.nonopn.skydome.common.app.api.impl.ApiInterface
import com.nonopn.skydome.common.app.pages.main.model.api.AeroguardLocation
import com.nonopn.skydome.common.app.pages.main.model.api.ApiAnswerProjectWithThunderActivities
import com.nonopn.skydome.common.app.pages.main.model.api.ApiEstimatesData
import com.nonopn.skydome.common.app.pages.main.model.api.Probe
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.main.model.api.PulseType
import com.nonopn.skydome.common.app.pages.main.model.api.SectionProjectAndThunderDetection
import com.nonopn.skydome.common.app.pages.main.model.api.ThunderDetectionActivity
import com.nonopn.skywatcher.design.widgets.endOfDay
import com.nonopn.skywatcher.design.widgets.startOfDay
import korlibs.time.DateFormat
import korlibs.time.DateTime
import korlibs.time.DateTimeTz
import korlibs.time.days
import korlibs.time.minutes

class LoadStrikeRadarData(private var currentInterface: ApiInterface) {
    private var id = 0

    class LoadStrikeRadarCanceledException : Throwable("already loading data")

    data class Result(
        val lightningActivity: Map<String, ThunderDetectionActivity>,
        val probes: List<Probe>,
        val projects: List<Project>,
        val aeroguardLocations: List<AeroguardLocation>?
    )

    suspend fun startLoadingEstimatesData(): List<ApiEstimatesData> = currentInterface.estimatesData()

    suspend fun startLoadingLightning(
        date: DateTimeTz,
        fullDay: Boolean,
        overrideTest: Boolean = false,
        rangeFrom: DateTimeTz? = null,
        rangeTo: DateTimeTz? = null
    ): Result {
        val currentId = ++id

        val (start, end) = when {
            rangeFrom != null && rangeTo != null -> rangeFrom to rangeTo
            overrideTest -> {
                val startOverride = DateTime.now().minus(10.days)
                val endOverride = DateTime.now()
                startOverride.local to endOverride.local
            }
            else -> {
                val endTrue = if (fullDay) date.endOfDay else date
                val startTrue = if (fullDay) endTrue.startOfDay else endTrue.minus(30.minutes)
                startTrue to endTrue
            }
        }

        println(
            "will load from ${start.utc.format(DateFormat.FORMAT1)} " +
                    "to ${end.utc.format(DateFormat.FORMAT1)}"
        )

        val body = currentInterface.probes(start, end)

        if (currentId != id) {
            throw LoadStrikeRadarCanceledException()
        } else {
            println("loaded from ${date.format(DateFormat.FORMAT2)}")
        }

        val lightningActivity = body.impacts.filter { it.pulseType == PulseType.CG }
        val projects = body.projects
        val probes = body.probes

        val filtered = mutableMapOf<String, ThunderDetectionActivity>()
        lightningActivity.forEach { filtered[it.externalId] = it }

        return Result(
            lightningActivity = filtered,
            probes = probes,
            projects = projects,
            aeroguardLocations = body.aeroguardPoints
        )
    }

    suspend fun startLoadingLightningForCertificates(
        date: DateTimeTz,
        fullDay: Boolean,
        overrideTest: Boolean = false
    ): List<ApiAnswerProjectWithThunderActivities> {
        val currentId = ++id

        val (start, end) = if (overrideTest) {
            val startOverride = DateTime.now().minus(15.days)
            val endOverride = DateTime.now()

            startOverride.local to endOverride.local
        } else {
            val endTrue = if (fullDay) {
                date.endOfDay
            } else {
                date
            }

            val startTrue = if (fullDay) {
                endTrue.startOfDay
            } else {
                endTrue.minus(30.minutes)
            }

            startTrue to endTrue
        }

        println(
            "will load from ${start.utc.format(DateFormat.FORMAT1)} " +
                    "to ${end.utc.format(DateFormat.FORMAT1)}"
        )

        val body = currentInterface.certificateActivities(start, end)

        if (currentId != id) {
            throw LoadStrikeRadarCanceledException()
        } else {
            println("loaded from ${date.format(DateFormat.FORMAT2)}")
        }

        return body
    }

    suspend fun startLoadingLightningForDensitiesCertificates(
        date: DateTimeTz,
        fullDay: Boolean,
        overrideTest: Boolean = false
    ): List<SectionProjectAndThunderDetection> {
        val currentId = ++id

        val (start, end) = if (overrideTest) {
            val startOverride = DateTime.now().minus(15.days)
            val endOverride = DateTime.now()

            startOverride.local to endOverride.local
        } else {
            val endTrue = if (fullDay) {
                date.endOfDay
            } else {
                date
            }

            val startTrue = if (fullDay) {
                endTrue.startOfDay
            } else {
                endTrue.minus(30.minutes)
            }

            startTrue to endTrue
        }

        println(
            "will load from ${start.utc.format(DateFormat.FORMAT1)} " +
                    "to ${end.utc.format(DateFormat.FORMAT1)}"
        )

        val body = currentInterface.certificateDensityActivities(start, end)

        if (currentId != id) {
            throw LoadStrikeRadarCanceledException()
        } else {
            println("loaded from ${date.format(DateFormat.FORMAT2)}")
        }

        return body
    }
}
